import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { chatAPI } from '../api/client';

// Derive socket URL from REACT_APP_API_URL so it works in production without a separate env var
const SOCKET_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api\/?$/, '');

export function useActivityChat(activityId) {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  // REST poll — used as fallback when socket is not connected
  const pollMessages = useCallback(() => {
    if (!activityId) return;
    chatAPI.getMessages(activityId).then((res) => {
      if (mountedRef.current) setMessages(res.data || []);
    }).catch(() => {});
  }, [activityId]);

  useEffect(() => {
    if (!activityId) return;
    mountedRef.current = true;

    // Load history immediately
    pollMessages();

    const socket = io(SOCKET_URL, {
      auth: { token: localStorage.getItem('token') },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      timeout: 8000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      if (!mountedRef.current) return;
      setConnected(true);
      clearInterval(pollRef.current);
      socket.emit('join_activity', activityId);
    });

    socket.on('new_message', (msg) => {
      if (mountedRef.current) setMessages((prev) => [...prev, msg]);
    });

    socket.on('disconnect', () => {
      if (!mountedRef.current) return;
      setConnected(false);
      // Fall back to polling every 5s when socket drops
      pollRef.current = setInterval(pollMessages, 5000);
    });

    socket.on('connect_error', () => {
      if (!mountedRef.current) return;
      setConnected(false);
      // Start REST polling if socket can't connect
      if (!pollRef.current) {
        pollRef.current = setInterval(pollMessages, 5000);
      }
    });

    return () => {
      mountedRef.current = false;
      clearInterval(pollRef.current);
      pollRef.current = null;
      socket.emit('leave_activity', activityId);
      socket.disconnect();
    };
  }, [activityId, pollMessages]);

  const sendMessage = useCallback(async (content) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('send_message', { activityId, content });
    } else {
      try {
        await chatAPI.postMessage(activityId, content);
        pollMessages();
      } catch {}
    }
  }, [activityId, pollMessages]);

  const deleteMessage = useCallback(async (messageId) => {
    try {
      await chatAPI.deleteMessage(activityId, messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch {}
  }, [activityId]);

  return { messages, sendMessage, deleteMessage, connected: true };
}
